import { Text } from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";
import { isWithinInterval } from "date-fns";
import { useAtom, useAtomValue } from "jotai";
import { EApprovalStatus, EBenefitContentTab, EFilterStatusType } from "library/types";
import { Trans, useTranslation } from "react-i18next";
import { selectedCategoryFilterAtom, selectedStatusFilterAtom } from "store";
import { benefitContentTabAtom, selectedPackageUserAtom } from "store/UIBenefitsPage";
import styled from "styled-components";

import { BenefitCard } from "components/BenefitCard/BenefitCard";
import { CardListWrap } from "components/styled";
import { useBenefitsData } from "hooks/useBenefitsData";

export function ExpiredPackages() {
  const { t } = useTranslation();
  const { packageData } = useBenefitsData();
  const categoryFilters = useAtomValue(selectedCategoryFilterAtom);
  const statusFilters = useAtomValue(selectedStatusFilterAtom);

  const [selectedContentTab, setSelectedContentTab] = useAtom(benefitContentTabAtom);
  const selectedPackageUser = useAtomValue(selectedPackageUserAtom);
  const allPackages = [...packageData.eligiblePackages, ...packageData.enrolledPackages];

  const filteredData = allPackages?.filter((item) => {
    const expired = !isWithinInterval(new Date(), {
      start: new Date(item?.validFrom || ""),
      end: new Date(item?.validTo || ""),
    });
    if (!expired) return false;

    const categoryFilterPassed = categoryFilters[selectedContentTab]?.find(
      (filter) => Number(filter.id) === item.benefitCategoryTag
    );

    const inReviewFilterSelected = statusFilters[selectedContentTab]?.find(
      (filter) => filter.id === EFilterStatusType.IN_REVIEW
    );

    const requiresEnrollmentApproval = item?.requiresEnrollmentApproval;
    const hasApprovePendingStatus = item?.approveEnrollStatusCode === EApprovalStatus.PENDING;
    const waitingForApproval =
      requiresEnrollmentApproval && !!item?.dateOfEnrollment && hasApprovePendingStatus;

    const requiresCancellationApproval = item?.requiresCancellationApproval;
    const hasCancelPendingStatus = item?.cancelEnrollApprovalStatusCode === EApprovalStatus.PENDING;
    const waitingForCancellation =
      requiresCancellationApproval && !!item?.dateOfCancellation && hasCancelPendingStatus;

    const inReviewFilterPassed = inReviewFilterSelected
      ? waitingForApproval || waitingForCancellation
      : true;

    if (item.isDependent) {
      return (
        item.employeeDependentTag === selectedPackageUser?.value &&
        categoryFilterPassed &&
        inReviewFilterPassed
      );
    }
    return (
      item.employeeLoginEmailAddress === selectedPackageUser?.value &&
      categoryFilterPassed &&
      inReviewFilterPassed
    );
  });

  return (
    <>
      {filteredData?.length ? (
        <CardListWrap>
          {filteredData.map((benefit) => {
            return (
              <BenefitCard selected key={benefit.benefitPackageTag} benefitPackage={benefit} />
            );
          })}
        </CardListWrap>
      ) : (
        <NoEnrolledBenefitsText>
          <div>
            <InfoRegular fontSize={18} />
          </div>
          <Text weight="semibold" block>
            <Trans
              t={t}
              i18nKey={"hbh.noExpiredPackages.label"}
              components={[
                <LinkToEligible
                  onClick={() => {
                    setSelectedContentTab(EBenefitContentTab.eligible);
                  }}
                />,
              ]}
            />
          </Text>
        </NoEnrolledBenefitsText>
      )}
    </>
  );
}

const NoEnrolledBenefitsText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  max-width: 600px;
`;

const LinkToEligible = styled.span`
  color: ${({ theme }) => theme.palette.green};
  text-decoration: underline;
  cursor: pointer;
`;
